import {
    useEffect,
    useMemo,
    useState,
} from 'react'

import {
    noop,
} from 'lodash'
import {
    FormPart,
} from 'app/types/form.types'
import useStyles from '../BatchActions.shared.styles'
import UploadFeedback from '../components/UploadFeedback'
import {
    FeedbackFormData,
} from '../components/UploadFeedback/UploadFeedback.types'
import useBatchUpload from '../hooks/useBatchUpload/useBatchUpload'
import {
    DataUpdateForm,
    ApiDataLoader,
} from './components'
import {
    BatchUpdateProps,
    UpdateFormData,
} from './BatchUpdate.types'

const BatchUpdate = <T extends Record<string, any>>({
    entity,
    uniqField = 'id' as Extract<keyof T, string>,
    dataRequest,
    dataRequestKey,
    uploadParamsMapper,
    filterParams,
    dataUpdateFormFields,
    feedbackFormFields,
    errorFeedbackColumns,
    onCancel,
    onUpdateFormChange = noop,
    updateFormValue: externalUpdateFormValue,
    dataUpdateFormInfoComponent,
    classNames = {},
}: Readonly<BatchUpdateProps<T>>) => {
    const {
        classes,
    } = useStyles()

    const [
        data,
        setData,
    ] = useState<Record<string, any>[] | null>(null)

    const defaultUpdateFormValue: UpdateFormData = useMemo(() => {
        return {
            selectedRecords: data?.length,
        }
    }, [data?.length])

    const [
        updateFormValue,
        setUpdateFormValue,
    ] = useState<UpdateFormData>()

    useEffect(() => {
        setUpdateFormValue(defaultUpdateFormValue)
    }, [defaultUpdateFormValue])

    useEffect(() => {
        if (externalUpdateFormValue) {
            setUpdateFormValue(externalUpdateFormValue)
        }
    }, [externalUpdateFormValue])

    const {
        uploadRecords,
        stopUploadRecords,
        failedUploads,
        successfulUploads,
        isLoading: isUploadingRecords,
    } = useBatchUpload({
        uploadParamsMapper,
        plainData: data,
        entity,
    })

    const handleUpdateFormChange = (formData: UpdateFormData) => {
        onUpdateFormChange(formData as UpdateFormData & T)
        setUpdateFormValue(formData)
    }

    const feedbackFormValue: FeedbackFormData = useMemo(() => {
        return {
            selectedRecords: data?.length,
            updatedRecords: successfulUploads,
            ...updateFormValue,
        }
    }, [
        data?.length,
        successfulUploads,
        updateFormValue,
    ])

    const uploadStepVisible = useMemo(() => {
        return failedUploads?.length > 0 || successfulUploads > 0
    }, [
        failedUploads,
        successfulUploads,
    ])

    return (
        <div className={classes.container}>
            {data === null && (
                <ApiDataLoader
                    request={dataRequest}
                    requestKey={dataRequestKey}
                    filterParams={filterParams}
                    onCancel={onCancel}
                    onLoadData={setData}
                />
            )}

            {(data !== null && !uploadStepVisible) && (
                <>
                    {dataUpdateFormInfoComponent}
                    <DataUpdateForm
                        formValue={updateFormValue}
                        formFields={dataUpdateFormFields as FormPart<keyof UpdateFormData>[]}
                        onFormChange={handleUpdateFormChange}
                        onSubmit={uploadRecords}
                        onCancel={onCancel}
                        classNames={classNames.dataUpdateForm}
                    />
                </>
            )}

            {uploadStepVisible && (
                <UploadFeedback
                    uniqField={uniqField}
                    formFields={feedbackFormFields as FormPart<keyof FeedbackFormData>[]}
                    columns={errorFeedbackColumns}
                    failedUploads={failedUploads}
                    feedbackFormValue={feedbackFormValue}
                    isLoading={isUploadingRecords}
                    onStopUpdate={stopUploadRecords}
                    onDoneClick={onCancel}
                />
            )}
        </div>
    )
}

export default BatchUpdate
